import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Play, Plus, Edit, Trash2, Video, Loader2, BarChart3, Star, Award } from "lucide-react";

interface Video {
  id: string;
  title: string;
  description: string;
  video_url: string;
  category: string;
  tags: string[];
  created_at: string;
}

interface PracticeVideo {
  id: string;
  title: string;
  description: string;
  video_url: string;
  status: string;
  created_at: string;
  feedback_count?: number;
  courses?: { title: string } | null;
}

interface VideoCollection {
  id: string;
  name: string;
  description: string;
  video_ids: string[];
  practice_video_ids?: string[];
  created_at: string;
  videos?: Video[];
  practice_videos?: PracticeVideo[];
}

interface VideoCollectionProps {
  portfolioId?: string;
  isOwner: boolean;
  isPublic?: boolean;
}

export function VideoCollection({ portfolioId, isOwner, isPublic = false }: VideoCollectionProps) {
  const { toast } = useToast();
  const [collections, setCollections] = useState<VideoCollection[]>([]);
  const [availableVideos, setAvailableVideos] = useState<Video[]>([]);
  const [availablePracticeVideos, setAvailablePracticeVideos] = useState<PracticeVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState<VideoCollection | null>(null);
  
  const [newCollection, setNewCollection] = useState({
    name: "",
    description: "",
    video_ids: [] as string[],
    practice_video_ids: [] as string[]
  });

  useEffect(() => {
    loadCollections();
    if (isOwner) {
      loadAvailableVideos();
      loadAvailablePracticeVideos();
    }
  }, [portfolioId, isOwner]);

  const loadCollections = async () => {
    try {
      let query = supabase
        .from('video_collections')
        .select('*')
        .order('created_at', { ascending: false });

      if (portfolioId) {
        query = query.eq('portfolio_id', portfolioId);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Load videos and practice videos for each collection
      const collectionsWithVideos = await Promise.all(
        (data || []).map(async (collection) => {
          let videos: Video[] = [];
          let practiceVideos: PracticeVideo[] = [];

          // Load regular videos
          if (collection.video_ids && collection.video_ids.length > 0) {
            const { data: videoData } = await supabase
              .from('videos')
              .select('*')
              .in('id', collection.video_ids)
              .eq('status', 'active');
            videos = videoData || [];
          }

          // Load practice videos with coaching feedback
          const practiceVideoIds = (collection as any).practice_video_ids;
          if (practiceVideoIds && practiceVideoIds.length > 0) {
            const { data: practiceVideoData } = await supabase
              .from('practice_videos')
              .select(`
                id,
                title,
                description,
                video_url,
                status,
                created_at,
                courses!left (title),
                coaching_feedback!left (id)
              `)
              .in('id', practiceVideoIds)
              .eq('status', 'analyzed');

            practiceVideos = practiceVideoData?.map(video => ({
              ...video,
              feedback_count: video.coaching_feedback?.length || 0
            })) || [];
          }
          
          return { ...collection, videos, practice_videos: practiceVideos };
        })
      );

      setCollections(collectionsWithVideos);
    } catch (error) {
      console.error('Error loading collections:', error);
      toast({
        title: "Error",
        description: "Failed to load video collections",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableVideos = async () => {
    try {
      const { data, error } = await supabase
        .from('videos')
        .select('*')
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAvailableVideos(data || []);
    } catch (error) {
      console.error('Error loading videos:', error);
    }
  };

  const loadAvailablePracticeVideos = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('practice_videos')
        .select(`
          id,
          title,
          description,
          video_url,
          status,
          created_at,
          courses!left (title),
          coaching_feedback!left (id)
        `)
        .eq('user_id', user.id)
        .eq('status', 'analyzed')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const videosWithFeedback = data?.map(video => ({
        ...video,
        feedback_count: video.coaching_feedback?.length || 0
      })) || [];

      setAvailablePracticeVideos(videosWithFeedback);
    } catch (error) {
      console.error('Error loading practice videos:', error);
    }
  };

  const handleCreateCollection = async () => {
    if (!newCollection.name.trim()) {
      toast({
        title: "Error",
        description: "Please enter a collection name",
        variant: "destructive"
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('video_collections')
        .insert({
          portfolio_id: portfolioId,
          name: newCollection.name,
          description: newCollection.description,
          video_ids: newCollection.video_ids,
          practice_video_ids: newCollection.practice_video_ids
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Video collection created successfully"
      });

      setShowCreateDialog(false);
      setNewCollection({ name: "", description: "", video_ids: [], practice_video_ids: [] });
      loadCollections();
    } catch (error) {
      console.error('Error creating collection:', error);
      toast({
        title: "Error",
        description: "Failed to create video collection",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">Video Collections</h3>
          <p className="text-sm text-muted-foreground">
            Organize your videos into collections
          </p>
        </div>
        {isOwner && (
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add Collection
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Create Video Collection</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">Collection Name</Label>
                  <Input
                    id="name"
                    value={newCollection.name}
                    onChange={(e) => setNewCollection({...newCollection, name: e.target.value})}
                    placeholder="Enter collection name"
                  />
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={newCollection.description}
                    onChange={(e) => setNewCollection({...newCollection, description: e.target.value})}
                    placeholder="Enter collection description"
                  />
                </div>
                
                {availableVideos.length > 0 && (
                  <div>
                    <Label>Select Regular Videos</Label>
                    <div className="border rounded p-3 max-h-40 overflow-y-auto space-y-2">
                      {availableVideos.map((video) => (
                        <div key={video.id} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id={`video-${video.id}`}
                            checked={newCollection.video_ids.includes(video.id)}
                            onChange={(e) => {
                              const videoIds = e.target.checked
                                ? [...newCollection.video_ids, video.id]
                                : newCollection.video_ids.filter(id => id !== video.id);
                              setNewCollection({...newCollection, video_ids: videoIds});
                            }}
                          />
                          <label htmlFor={`video-${video.id}`} className="text-sm cursor-pointer">
                            {video.title}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {availablePracticeVideos.length > 0 && (
                  <div>
                    <Label>Select AI Coaching Videos</Label>
                    <div className="border rounded p-3 max-h-40 overflow-y-auto space-y-2">
                      {availablePracticeVideos.map((video) => (
                        <div key={video.id} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id={`practice-${video.id}`}
                            checked={newCollection.practice_video_ids.includes(video.id)}
                            onChange={(e) => {
                              const practiceVideoIds = e.target.checked
                                ? [...newCollection.practice_video_ids, video.id]
                                : newCollection.practice_video_ids.filter(id => id !== video.id);
                              setNewCollection({...newCollection, practice_video_ids: practiceVideoIds});
                            }}
                          />
                          <label htmlFor={`practice-${video.id}`} className="text-sm cursor-pointer flex items-center gap-2">
                            {video.title}
                            <Badge variant="secondary" className="text-xs">
                              {video.feedback_count} feedback
                            </Badge>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex justify-end space-x-2">
                  <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleCreateCollection}>
                    Create Collection
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {collections.length === 0 ? (
        <Card>
          <CardContent className="text-center py-8">
            <Video className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No video collections yet</p>
            {isOwner && (
              <p className="text-sm text-muted-foreground mt-2">
                Create your first collection to organize your videos
              </p>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6">
          {collections.map((collection) => (
            <Card key={collection.id}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      {collection.name}
                      <Badge variant="outline">
                        {(collection.videos?.length || 0) + (collection.practice_videos?.length || 0)} videos
                      </Badge>
                      {collection.practice_videos && collection.practice_videos.length > 0 && (
                        <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                          AI Coaching
                        </Badge>
                      )}
                    </CardTitle>
                    {collection.description && (
                      <CardDescription className="mt-1">
                        {collection.description}
                      </CardDescription>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {((collection.videos && collection.videos.length > 0) || (collection.practice_videos && collection.practice_videos.length > 0)) ? (
                  <div className="space-y-6">
                    {/* Regular Videos */}
                    {collection.videos && collection.videos.length > 0 && (
                      <div>
                        <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                          <Video className="h-4 w-4" />
                          Regular Videos ({collection.videos.length})
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {collection.videos.map((video) => (
                            <Card key={video.id} className="border-muted">
                              <CardContent className="p-4">
                                <h5 className="font-medium text-sm line-clamp-2 mb-2">
                                  {video.title}
                                </h5>
                                <p className="text-xs text-muted-foreground line-clamp-2 mb-3">
                                  {video.description}
                                </p>
                                <div className="flex flex-wrap gap-1 mb-3">
                                  <Badge variant="secondary" className="text-xs">
                                    {video.category}
                                  </Badge>
                                  {video.tags.slice(0, 2).map((tag, index) => (
                                    <Badge key={index} variant="outline" className="text-xs">
                                      {tag}
                                    </Badge>
                                  ))}
                                </div>
                                <Button size="sm" className="w-full">
                                  <Play className="h-3 w-3 mr-2" />
                                  Play
                                </Button>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* AI Coaching Videos */}
                    {collection.practice_videos && collection.practice_videos.length > 0 && (
                      <div>
                        <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                          <BarChart3 className="h-4 w-4" />
                          AI Coaching Videos ({collection.practice_videos.length})
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {collection.practice_videos.map((video) => (
                            <Card key={video.id} className="border-blue-200 bg-blue-50/50">
                              <CardContent className="p-4">
                                <h5 className="font-medium text-sm line-clamp-2 mb-2">
                                  {video.title}
                                </h5>
                                <p className="text-xs text-muted-foreground line-clamp-2 mb-3">
                                  {video.description}
                                </p>
                                <div className="flex flex-wrap gap-1 mb-3">
                                  <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-800">
                                    <Star className="h-3 w-3 mr-1" />
                                    {video.feedback_count} insights
                                  </Badge>
                                  {video.courses && (
                                    <Badge variant="outline" className="text-xs">
                                      {video.courses.title}
                                    </Badge>
                                  )}
                                </div>
                                <Button size="sm" className="w-full bg-blue-600 hover:bg-blue-700">
                                  <Play className="h-3 w-3 mr-2" />
                                  View with AI Insights
                                </Button>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-4">
                    No videos in this collection
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      
      {availablePracticeVideos.length > 0 && (
        <div className="mt-8 p-4 bg-green-50 rounded-lg border border-green-200">
          <h4 className="font-medium text-green-900 mb-2 flex items-center gap-2">
            <Award className="h-4 w-4" />
            ✨ AI Coaching Integration Active
          </h4>
          <p className="text-sm text-green-700">
            You can now add your practice videos with AI coaching feedback to your portfolio collections! 
            Your analyzed videos are available when creating new collections.
          </p>
        </div>
      )}
    </div>
  );
}